import fs from 'node:fs/promises'
import path from 'node:path'

import {tryFindStudioConfigPath} from '@sanity/cli-core'
import {watch as chokidarWatch, type FSWatcher} from 'chokidar'

import {buildDebug} from './buildDebug.js'
import {decorateIndexWithAutoGeneratedWarning} from './decorateIndexWithAutoGeneratedWarning.js'
import {decorateIndexWithBridgeScript} from './decorateIndexWithBridgeScript.js'
import {decorateIndexWithStagingScript} from './decorateIndexWithStagingScript.js'
import {getEntryModule} from './getEntryModule.js'
import {getPossibleDocumentComponentLocations} from './getPossibleDocumentComponentLocations.js'
import {renderDocument} from './renderDocument.js'

interface RuntimeOptions {
  cwd: string
  reactStrictMode: boolean
  watch: boolean

  appTitle?: string
  basePath?: string
  entry?: string
  isApp?: boolean
}

/**
 * Generates the `.sanity/runtime` directory, and optionally watches for custom
 * document files, rebuilding when they change
 *
 * @param options - Current working directory (Sanity root dir), and whether or not to watch
 * @returns A watcher instance if watch is enabled, undefined otherwise
 * @internal
 */
export async function writeSanityRuntime(options: RuntimeOptions): Promise<FSWatcher | undefined> {
  const {appTitle, basePath, cwd, entry, isApp, reactStrictMode, watch} = options
  const runtimeDir = path.join(cwd, '.sanity', 'runtime')

  buildDebug('Making runtime directory')
  await fs.mkdir(runtimeDir, {recursive: true})

  async function renderAndWriteDocument() {
    buildDebug('Rendering document template')
    const indexHtml = decorateIndexWithStagingScript(
      decorateIndexWithBridgeScript(
        decorateIndexWithAutoGeneratedWarning(
          await renderDocument({
            isApp,
            props: {
              basePath: basePath || '/',
              entryPath: `/${toForwardSlashes(path.relative(cwd, path.join(runtimeDir, 'app.js')))}`,
              title: appTitle,
            },
            studioRootPath: cwd,
          }),
        ),
      ),
    )

    buildDebug('Writing index.html to runtime directory')
    await fs.writeFile(path.join(runtimeDir, 'index.html'), indexHtml)
  }

  let watcher: FSWatcher | undefined

  if (watch) {
    watcher = chokidarWatch(getPossibleDocumentComponentLocations(cwd)).on('all', () =>
      renderAndWriteDocument(),
    )
  }

  await renderAndWriteDocument()

  buildDebug('Writing app.js to runtime directory')
  let relativeConfigLocation: string | null = null
  if (!isApp) {
    const studioConfigPath = await tryFindStudioConfigPath(cwd)
    relativeConfigLocation = studioConfigPath
      ? toForwardSlashes(path.relative(runtimeDir, studioConfigPath))
      : null
  }

  const relativeEntry = toForwardSlashes(
    path.relative(runtimeDir, path.resolve(cwd, entry || './src/App')),
  )
  const appJsContent = getEntryModule({
    basePath,
    entry: relativeEntry,
    isApp,
    reactStrictMode,
    relativeConfigLocation,
  })
  await fs.writeFile(path.join(runtimeDir, 'app.js'), appJsContent)

  return watcher
}

function toForwardSlashes(filePath: string): string {
  return filePath.replaceAll('\\', '/')
}
